import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { isDemo, isMock, isProduction } from "../api/mode.ts";
import { IconAlert, IconBook, IconInfo, IconClose, IconClock, IconCode, IconDown, IconExternal, IconHome, IconLedger, IconList, IconVault, IconLogout, IconMenu, IconSettings, IconShield, IconUp, IconUsers } from "../components/Icons.tsx";
import { useMedia } from "../lib/hooks.ts";
import { useMe, useSession } from "../lib/session.tsx";
import type { Permission } from "../api/types.ts";
import { BrandSwitcher } from "./BrandSwitcher.tsx";
import { QuickLookup } from "./QuickLookup.tsx";
import { Tour } from "./Tour.tsx";
import "./user-menu.css";
import { LanguagePicker } from "../components/LanguagePicker.tsx";
import { fmt, rich, useT, type Dict } from "../i18n/index.tsx";
import { logo, mark } from "../assets/images.ts";

/** Nav items; shown when the user has any of `perms` for the active brand. `label` is a key of `t.shell.nav`. */
/** `label`: a key of shell.nav, or a function for areas that keep their own copy (vault). */
export const NAV: { to: string; label: keyof Dict["shell"]["nav"] | ((t: Dict) => string); icon: () => ReactNode; perms: Permission[] }[] = [
  { to: "/", label: "overview", icon: () => <IconHome />, perms: ["overview:view"] },
  { to: "/deposits", label: "deposits", icon: () => <IconDown />, perms: ["deposits:view"] },
  { to: "/payouts", label: "payouts", icon: () => <IconUp />, perms: ["payouts:view"] },
  { to: "/statement", label: "statement", icon: () => <IconLedger />, perms: ["statement:view"] },
  { to: "/vault", label: (t) => t.vault.nav, icon: () => <IconVault />, perms: ["vault:view"] },
  { to: "/developers", label: "developers", icon: () => <IconCode />, perms: ["api_keys:view", "webhook:view"] },
  { to: "/team", label: "team", icon: () => <IconUsers />, perms: ["team:view"] },
  { to: "/activity", label: "activity", icon: () => <IconList />, perms: ["activity:view"] },
  { to: "/settings", label: "settings", icon: () => <IconSettings />, perms: ["settings:view"] },
];

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";

export function AppShell() {
  const t = useT();
  const me = useMe();
  const { canAny, logout } = useSession();
  const [open, setOpen] = useState(false);
  const [tourKick, setTourKick] = useState(0);
  const phone = useMedia("(max-width: 960px)");
  const loc = useLocation();
  const navigate = useNavigate();
  const drawerRef = useRef<HTMLElement>(null);
  const menuBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => setOpen(false), [loc.pathname]);
  // The How-to page's "Start the guide again" button.
  useEffect(() => {
    const kickTour = () => setTourKick((k) => k + 1);
    window.addEventListener("icn:tour", kickTour);
    return () => window.removeEventListener("icn:tour", kickTour);
  }, []);
  useEffect(() => {
    if (!phone) setOpen(false);
  }, [phone]);

  // Drawer: focus the first link when it opens, Escape closes, focus goes back to the menu button.
  useEffect(() => {
    if (!open) return;
    drawerRef.current?.querySelector<HTMLElement>("a, button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        menuBtn.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.documentElement.classList.add("drawer-open");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.documentElement.classList.remove("drawer-open");
    };
  }, [open]);

  const doLogout = async () => {
    await logout().catch(() => {});
    navigate("/login", { replace: true });
  };

  const env = isProduction ? null : t.shell.testnet;
  const sidebarHidden = phone && !open;

  return (
    <div className="shell">
      <a href="#main" className="skip-link">{t.shell.skipToContent}</a>
      <aside
        ref={drawerRef}
        className={`sidebar ${open ? "open" : ""}`}
        aria-label={t.shell.mainNavigation}
        inert={sidebarHidden || undefined}
        aria-hidden={sidebarHidden || undefined}
      >
        <div className="sidebar-top">
          <NavLink to="/" className="sidebar-brand" aria-label={t.shell.homeLabel}>
            <img src={logo} alt="iCryptoNow" width={160} height={27} />
          </NavLink>
          {phone && (
            <button type="button" className="icon-btn" onClick={() => setOpen(false)} aria-label={t.shell.closeMenu}>
              <IconClose />
            </button>
          )}
        </div>
        <div className="sidebar-label">{t.shell.console}</div>
        <nav className="side-nav">
          {NAV.filter((n) => canAny(...n.perms)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
              {n.icon()}
              <span>{typeof n.label === "function" ? n.label(t) : t.shell.nav[n.label]}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <NavLink to="/help" className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <IconInfo />
            <span>{t.help.nav}</span>
          </NavLink>
          <NavLink to="/settings/security" className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <IconShield />
            <span>{t.shell.security}</span>
          </NavLink>
          <a href="https://icryptonow.com/docs/" target="_blank" rel="noreferrer noopener" className="side-link">
            <IconBook />
            <span>{t.common.apiDocs}</span>
          </a>
        </div>
      </aside>
      {phone && open && <div className="scrim" onClick={() => setOpen(false)} aria-hidden="true" />}

      <div className="main-col" inert={(phone && open) || undefined}>
        {me.merchant.payouts_paused && (
          <div className="paused-banner" role="alert">
            <IconAlert size={18} />
            <span>{rich(t.shell.pausedBanner)}</span>
          </div>
        )}
        {isDemo && (
          <div className="review-banner" role="note">
            <IconAlert size={18} />
            <span>{t.shell.demoBanner}</span>
          </div>
        )}
        {env && !isMock && (
          <div className="review-banner" role="note">
            <IconAlert size={18} />
            <span>{t.shell.testnetBanner}</span>
          </div>
        )}
        {me.merchant.in_review && (
          <div className="review-banner" role="status">
            <IconClock size={18} />
            <span>{rich(t.shell.inReviewBanner, { a: (c) => <a href="https://t.me/mrho68" target="_blank" rel="noreferrer noopener">{c}</a> })}</span>
          </div>
        )}
        <header className="topbar">
          <button ref={menuBtn} type="button" className="icon-btn menu-toggle" onClick={() => setOpen(true)} aria-label={t.shell.openMenu} aria-expanded={open}>
            <IconMenu />
          </button>
          <NavLink to="/" className="topbar-mark" aria-label={t.shell.homeLabel}>
            <img src={mark} alt="" width={28} height={28} />
          </NavLink>
          <BrandSwitcher />
          <QuickLookup />
          <div className="topbar-right">
            {isMock && <span className="env-badge env-mock" title={t.shell.mockDataTitle}>{t.shell.mockData}</span>}
            {env && <span className="env-badge" title={t.shell.testnetTitle}>{env}</span>}
            <LanguagePicker compact={phone} />
            <UserMenu onLogout={doLogout} onTour={() => setTourKick((k) => k + 1)} />
          </div>
        </header>
        <main id="main" className="content" tabIndex={-1}>
          {/* Keyed by brand: switching brands remounts the page so it loads that brand's data. */}
          <Outlet key={me.merchant.id} />
        </main>
      </div>
      <Tour kick={tourKick} />
    </div>
  );
}

/** Top-right account menu: avatar button → name, role, email, Security settings, Log out. */
function UserMenu({ onLogout, onTour }: { onLogout: () => void; onTour: () => void }) {
  const t = useT();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const loc = useLocation();
  // Built-in roles show in the console language; custom role names are the merchant's own text.
  const role = me.role.key ? t.common.roles[me.role.key] : me.role.name;

  useEffect(() => setOpen(false), [loc.pathname]);

  // Open: focus the first item; click outside or Escape closes (Escape returns focus to the button).
  useEffect(() => {
    if (!open) return;
    ref.current?.querySelector<HTMLElement>(".user-menu-panel a, .user-menu-panel button")?.focus();
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={ref}>
      <button
        ref={btn}
        type="button"
        className="user-btn"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={fmt(t.shell.accountLabel, { name: me.user.name })}
      >
        <span className="avatar sm" aria-hidden="true">{initials(me.user.name)}</span>
        <span className="user-btn-text">
          <strong>{me.user.name}</strong>
          <span>{role}</span>
        </span>
        <svg className="user-btn-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="user-menu-panel" role="menu" aria-label={t.shell.account}>
          <div className="user-menu-head">
            <span className="avatar" aria-hidden="true">{initials(me.user.name)}</span>
            <div>
              <strong>{me.user.name}</strong>
              <span className="user-menu-role">{role} · {me.organization.name}</span>
              <span className="user-menu-email" title={me.user.email}>{me.user.email}</span>
              {me.organization.customer_number && (
                <span className="user-menu-email">{fmt(t.shell.customerNo, { number: me.organization.customer_number })}</span>
              )}
            </div>
          </div>
          {me.organization.support_group_url && (
            <a href={me.organization.support_group_url} target="_blank" rel="noreferrer noopener" role="menuitem" className="user-menu-item">
              <IconUsers size={18} />
              <span className="user-menu-grow">{t.shell.tgGeneral}</span>
              <IconExternal size={14} />
            </a>
          )}
          {me.organization.finance_group_url && (
            <a href={me.organization.finance_group_url} target="_blank" rel="noreferrer noopener" role="menuitem" className="user-menu-item user-menu-item-2line">
              <IconUsers size={18} />
              <span className="user-menu-grow">
                {t.shell.tgFinance}
                <small>{t.shell.tgFinanceHint}</small>
              </span>
              <IconExternal size={14} />
            </a>
          )}
          <NavLink to="/settings/security" role="menuitem" className="user-menu-item">
            <IconShield size={18} />
            {t.shell.securitySettings}
          </NavLink>
          <button type="button" role="menuitem" className="user-menu-item" onClick={() => { setOpen(false); onTour(); }}>
            <IconBook size={18} />
            {t.tour.menu}
          </button>
          <button type="button" role="menuitem" className="user-menu-item danger" onClick={onLogout}>
            <IconLogout size={18} />
            {t.shell.logOut}
          </button>
        </div>
      )}
    </div>
  );
}
